import { Component, OnInit } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { RuntimeBootstrapService } from '../../services/runtime-bootstrap.service';
import { ClientService } from '../../services/client.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { take } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-login',
    imports: [CommonModule, FormsModule],
    templateUrl: './login.component.html',
    styles: [`
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      .landing-page { animation: fadeIn 0.5s ease-out; }

      /* Animated brand / accent gradient */
      @keyframes gradientShift {
        0%, 100% { background-position: 0% 50%; }
        50%      { background-position: 100% 50%; }
      }
      .zavo-gradient-text {
        background: linear-gradient(90deg, #f97316, #a855f7, #6366f1, #a855f7, #f97316);
        background-size: 300% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: gradientShift 8s ease infinite;
      }

      /* Soft floating glow blobs in the hero */
      @keyframes blobFloat {
        0%, 100% { transform: translate(0, 0) scale(1); }
        33%      { transform: translate(30px, -20px) scale(1.05); }
        66%      { transform: translate(-20px, 20px) scale(0.97); }
      }
      .zavo-blob { animation: blobFloat 14s ease-in-out infinite; }

      /* Subtle card lift */
      .zavo-card { transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease; }
      .zavo-card:hover {
        transform: translateY(-4px);
        border-color: rgba(168,85,247,0.4);
        box-shadow: 0 20px 40px -20px rgba(168,85,247,0.35);
      }

      @keyframes modalPop {
        from { opacity: 0; transform: translateY(12px) scale(0.97); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }
      .zavo-modal { animation: modalPop 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    `]
})
export class LoginComponent implements OnInit {
  loginFlag = false;
  loginMode: 'email' | 'phone' = 'email';
  /** Shown when the single-session guard signed this browser out (the account logged in elsewhere). */
  kickedNotice = false;
  /** True in a deployed per-project artifact (isRuntime). The Zavo editor login form is hidden here —
   *  anonymous visitors are sent to the project's own landing page (its auth gate). */
  isArtifact = environment.isRuntime;

  constructor(
    private authService: AuthService,
    private router: Router,
    private bootstrap: RuntimeBootstrapService,
    private clientService: ClientService,
  ) {}

  ngOnInit(): void {
    // Single-session guard signed this browser out (the account logged in elsewhere) → show a durable
    // notice on the login screen (consume the one-shot flag the guard set before it navigated here).
    if (sessionStorage.getItem('zavo_kick_notice')) {
      this.kickedNotice = true;
      sessionStorage.removeItem('zavo_kick_notice');
    }
    // Editor build (isRuntime=false): nothing changes — show the normal login form.
    if (!environment.isRuntime) return;
    // Artifact: never show the Zavo login screen. If a session already exists, auth.service routes to
    // ui-editor. Otherwise send the visitor to the project's landing page (its login/signup blocks).
    // No landing page bundled → stay blank (the #4 blank fallback).
    this.authService.isAuthenticated().pipe(take(1)).subscribe((authed) => {
      // Signed in already. auth.service moves them into the app once it has resolved their project —
      // except when that project is ALREADY selected, where its handler returns early. That is the
      // state after accepting an invite on this app's own address (zavo_Sep_18): /accept-invite signs
      // the person in and sends them to '/', and they were left on a blank page. So go in from here
      // in exactly that case, and in no other: with no project selected yet, UiEditorComponent reloads
      // the page to '/', and going in early bounced every signed-in visitor between '/' and
      // /ui-editor forever (zavo_Sep_18 follow-up, 28 Sep).
      if (authed) {
        if (this.clientService.selectedClientId) {
          this.router.navigate(['ui-editor'], { replaceUrl: true });
        }
        return;
      }
      if (this.bootstrap.landingPage) {
        // replaceUrl: an ENTRY into the app must not leave a history entry behind. Going "back" into
        // it rebuilds UiEditorComponent on the default page and discards whatever the user was doing
        // — on Android via the back button, in a browser via its back button, on an iPhone home-screen
        // app via edge-swipe. Page-to-page moves inside the app create no history, so this is the only
        // history there is to fall back through.
        this.router.navigate(['landing'], { replaceUrl: true });
      }
    });
  }

  loginFlagToggle() {
    this.loginFlag = !this.loginFlag;
    if (this.loginFlag) {
      this.loginMode = 'email';
      this.forgotPasswordFlag = false;
      this.otpSent = false;
      this.otpCode = '';
    }
  }

// login shit

  emailId = '';
  forgotEmailId = '';
  password = '';
  wrongEmailFlag = true;
  /** User-facing sign-in error (empty = no error shown). Surfaced in the login form. */
  loginError = '';
  isLoggingIn = false;

  phoneNumber = '';
  otpCode = '';
  otpSent = false;
  isSendingOtp = false;
  isVerifyingOtp = false;

  forgotPasswordFlag = false;
  /** Forgot-password feedback (empty = none shown). */
  forgotMessage = '';
  forgotError = '';
  isSendingReset = false;
  deliveryAddressCheckBoxFlag = true;
  exitScreen() {
    this.loginFlag = false;
    this.forgotPasswordFlag = false;
    this.loginMode = 'email';
    this.otpSent = false;
    this.otpCode = '';
  }

  switchLoginMode(mode: 'email' | 'phone') {
    this.loginMode = mode;
    this.forgotPasswordFlag = false;
    this.otpSent = false;
    this.otpCode = '';
  }
  async login() {
    this.loginError = '';
    if (!this.emailId?.trim() || !this.password) {
      this.loginError = 'Please enter your email and password.';
      return;
    }
    this.isLoggingIn = true;
    try {
      // Scope sign-in to the right Identity Platform pool:
      //   artifact runtime → the project's pool (from the /zavo-runtime.json bootstrap)
      //   editor           → the EDITOR pool (environment.editorPoolId) — builders live there, and
      //                      token.uid must match zavoConfig.project_members for the gateway builder-path.
      const authPoolId = environment.isRuntime
        ? this.authService.getRuntimeProject().authPoolId
        : (environment as any).editorPoolId;
      await this.authService.signInWithEmail(
        this.emailId.trim(),
        this.password,
        this.deliveryAddressCheckBoxFlag,
        authPoolId
      );
      await this.authService.ensureRuntimeClientClaim();
      if(environment.isRuntime) {
        this.router.navigate(['ui-editor'], { replaceUrl: true });   // see replaceUrl note above
      }
        else
      this.router.navigate(['/dashboard/client-list']);
    } catch (error: any) {
      this.loginError = this.mapAuthError(error);
      console.log('login failed:', error?.code || error?.message || error);
    } finally {
      this.isLoggingIn = false;
    }
  }

  /** Map a Firebase/Identity-Platform auth error to a friendly, user-facing message. */
  private mapAuthError(error: any): string {
    const code = (error?.code || error?.message || '').toString();
    if (code.includes('auth/invalid-credential') ||
        code.includes('auth/wrong-password') ||
        code.includes('auth/invalid-login-credentials')) {
      return 'Incorrect email or password. Please try again.';
    }
    if (code.includes('auth/user-not-found')) {
      return 'No account found with this email. Check the address or contact your admin.';
    }
    if (code.includes('auth/invalid-email')) {
      return 'That email address is not valid.';
    }
    if (code.includes('auth/user-disabled')) {
      return 'This account has been disabled. Please contact support.';
    }
    if (code.includes('auth/too-many-requests')) {
      return 'Too many attempts. Please wait a moment and try again.';
    }
    if (code.includes('auth/network-request-failed')) {
      return 'Network error. Check your connection and try again.';
    }
    if (code.includes('auth/missing-password')) {
      return 'Please enter your password.';
    }
    return 'Sign-in failed. Please try again.';
  }

  async sendOtp() {
    if (!this.phoneNumber?.trim()) {
      alert('Please enter your phone number');
      return;
    }

    if (!/^\+?[1-9]\d{7,14}$/.test(this.phoneNumber.trim())) {
      alert('Please enter a valid phone number in E.164 format (e.g. +14155552671)');
      return;
    }

    this.isSendingOtp = true;
    try {
      await this.authService.sendOtp(this.phoneNumber.trim(), 'recaptcha-container');
      this.otpSent = true;
    } catch (error) {
      console.log('failed to send otp', error);
      alert('Failed to send OTP. Please try again.');
      this.otpSent = false;
    } finally {
      this.isSendingOtp = false;
    }
  }

  async verifyOtp() {
    if (!this.otpCode?.trim()) {
      alert('Please enter the OTP code');
      return;
    }

    this.isVerifyingOtp = true;
    try {
      await this.authService.verifyOtp(this.otpCode.trim());
      await this.authService.ensureRuntimeClientClaim();

      if (environment.isRuntime) {
        this.router.navigate(['ui-editor'], { replaceUrl: true });   // see replaceUrl note above
      } else {
        this.router.navigate(['/dashboard/client-list']);
      }
    } catch (error) {
      console.log('otp verification failed', error);
      alert('OTP verification failed. Please try again.');
    } finally {
      this.isVerifyingOtp = false;
    }
  }
  forgotPassword() {
    this.forgotPasswordFlag = true;
    this.forgotMessage = '';
    this.forgotError = '';
  }
  forgotPasswordBack() {
    this.forgotPasswordFlag = false;
    this.forgotMessage = '';
    this.forgotError = '';
  }

  async resetPassword() {
    this.forgotMessage = '';
    this.forgotError = '';
    const email = this.forgotEmailId?.trim();
    if (!email) {
      this.forgotError = 'Please enter your email address.';
      return;
    }
    this.isSendingReset = true;
    try {
      // Scope the reset to the right Identity Platform pool — same rule as login():
      //   artifact runtime → the project's pool; editor → the EDITOR pool. Users live in a pool,
      //   not the default Auth, so an unscoped reset silently finds no account.
      const authPoolId = environment.isRuntime
        ? this.authService.getRuntimeProject().authPoolId
        : (environment as any).editorPoolId;
      await this.authService.sendPasswordResetEmail(email, authPoolId);
      this.forgotMessage =
        'If an account exists for that email, a reset link is on its way. Check your inbox.';
    } catch (error: any) {
      const code = (error?.code || error?.message || '').toString();
      // Don't reveal whether an account exists — show the same generic confirmation.
      if (code.includes('user-not-found')) {
        this.forgotMessage =
          'If an account exists for that email, a reset link is on its way. Check your inbox.';
      } else {
        this.forgotError = this.mapAuthError(error);
      }
      console.log('reset failed:', code || error);
    } finally {
      this.isSendingReset = false;
    }
  }

  // handle the passowrd showing capability
  togglePasswordVisibility() {
    const passwordInput = document.getElementById(
      'passwordInput'
    ) as HTMLInputElement;
    const eyeIcon = document.getElementById('eyeIcon') as HTMLElement;

    if (passwordInput && passwordInput.type === 'password') {
      passwordInput.type = 'text';
      eyeIcon.classList.add('text-blue-500');
      eyeIcon.classList.remove('text-gray-400');
    } else if (passwordInput) {
      passwordInput.type = 'password';
      eyeIcon.classList.remove('text-blue-500');
      eyeIcon.classList.add('text-gray-400');
    }
  }
}
